import { Link, useNavigate, useParams } from "react-router-dom"
import { Button, buttonVariants } from "../../components/ui/button"
import BackIcon from "../../components/back-icon"
import { Textarea } from "../../components/ui/textarea"
import { useDoctor } from "../../lib/hooks/doctors/use-doctor"
import { useSymptoms } from "../../lib/hooks/doctors/use-symptoms"
import { CircleUserRound } from "lucide-react"
import { useAuth, isTokenValid } from "../../contexts/auth-context"
import { usePatientsByMobile } from "../../lib/hooks/doctors/use-patients-by-mobile"
import { cn } from "../../lib/utils"
import { useAppointmentData } from "../../contexts/appointment-data-context"
import { format } from "date-fns"
import type { IPatient } from "../../types"
import { useToast } from "../../components/ui/use-toast"
import { useEffect, useState } from "react"
import { getCookie } from "../../contexts/auth-context"

import UpperNav from "../../components/upper-nav"

function AppointmentPage() {
  const { toast } = useToast()

  const { mobile, fetched } = useAuth()

  const navigate = useNavigate()
  const params = useParams()
  const professionalId = params.professionalId

  const { data, setData } = useAppointmentData()
  // console.log("Current appointment data:", data);
  const [typedSymptoms, setTypedSymptoms] = useState("")

  const { doctor, isLoading } = useDoctor(professionalId)
  // console.log("doctor:", doctor, "isLoading:", isLoading);
  const { patients } = usePatientsByMobile({})
  // console.log("patients : ", patients);

  const { symptoms = [], isLoading: isSymptomsLoading } = useSymptoms({
    enabled: !!(doctor?.specialityIds ?? []).length,
    specialityIds: doctor?.specialityIds || [],
  })

  function handleSelectPatient(patient: IPatient) {
    setData({
      ...data,
      patient: {
        id: patient.patientId,
        name: patient.name,
      },
      branchDetails: data?.branchDetails || [],
    })
  }

  function handleTypeSymptoms(symptoms: string) {
    const newSymptoms = symptoms.split(",").map((s) => s.trim())
    setData({
      ...data,
      symptoms: newSymptoms,
      branchDetails: data?.branchDetails || [],
    })
  }

  function handleToggleSelectSymptom(symptom: string) {
    setData({
      ...data,
      symptoms: data?.symptoms?.includes(symptom)
        ? data?.symptoms?.filter((s) => s !== symptom)
        : [...(data?.symptoms || []), symptom],
      branchDetails: data?.branchDetails || [],
    })
  }

  function handleContinue() {
    const refreshToken = getCookie("refreshToken");
    // Check if refresh token exists and is valid
    if (!refreshToken || !isTokenValid(refreshToken)) {
      // Redirect to verifyUrl if refresh token is missing or expired
      const apptUrl = `/doctors/${doctor?.professionalId}/appointment`;
      const verifyUrl = `/verify-patient?redirect=${apptUrl}`;
      navigate(verifyUrl);
      return;
    }

    // Existing logic for patient selection and navigation
    if (mobile && patients?.length && !data?.patient) {
      return toast({
        title: "Please select a patient to continue",
        variant: "destructive",
      });
    }

    if (mobile && !patients?.length && professionalId) {
      return navigate(
        `/add-patient?redirectUrl=/doctors/${professionalId}/appointment/confirm&backUrl=/doctors/${professionalId}/appointment`
      );
    }

    const apptUrl = `/doctors/${doctor?.professionalId}/appointment`;
    const confirmUrl = `${apptUrl}/confirm`;
    const verifyUrl = `/verify-patient?redirect=${apptUrl}`;

    navigate(fetched && mobile ? confirmUrl : verifyUrl);
  }

  useEffect(() => {
    if (!isLoading && (!data?.slotDate || !data.slotTiming || !data.dayOfWeek)) {
      navigate(`/doctors/${doctor?.professionalId}`)
    }
  }, [data, doctor?.professionalId, navigate, isLoading])

  return (
    <>
      {/* <UpperNav /> */}
      <div className="container py-1 mt-2 sm:mt-2 lg:mt-10 pb-24">
        <div className="mb-4 flex items-center gap-2">
          <Link
            to={`/doctors/${professionalId}`}
            className={buttonVariants({
              variant: "ghost",
              size: "icon",
            })}
          >
            <BackIcon />
          </Link>
          <h1 className="text-sm font-bold">Select Patient</h1>
        </div>

        <div className="space-y-6">
          <div className="flex gap-3">
            {doctor?.profilePhoto ? (
              <img
                src={`${doctor?.profilePhoto}`}
                alt={doctor?.fullName}
                className="size-16 rounded-full object-contain sm:size-20"
              />
            ) : (
              <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-muted sm:size-20">
                <CircleUserRound className="size-full " strokeWidth={1} />
              </div>
            )}
            <div className="w-full">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold sm:text-lg">Dr {doctor?.fullName}</h3>
                {/* <button className="text-xs font-bold text-primary">Change</button> */}
              </div>
              {doctor?.specialities?.length && (
                <p className="mb-1 text-xs text-muted-foreground sm:text-sm">{doctor?.specialities.join(", ")}</p>
              )}
              <p className="mb-1 text-xs font-bold text-muted-foreground">{doctor?.subDistrictOrCity}</p>
              {data?.slotDate && data.dayOfWeek && data.slotTiming && (
                <p className="mb-2 text-xs">
                  {data?.dayOfWeek}, {data?.slotTiming} {format(data?.slotDate, "d MMM yyyy")}
                </p>
              )}
              <p className="mb-2 text-sm font-bold">Fee : ₹{doctor?.fee}</p>
            </div>
          </div>

          <div className="mx-auto h-0.5 w-11/12 bg-border" />

          {!!patients?.length && (
            <div>
              <div className="mb-4 flex items-center justify-between gap-2">
                <h2 className="text-left font-bold">Select Patient</h2>
                <Link
                  to={`/add-patient?backUrl=/doctors/${professionalId}/appointment`}
                  className={buttonVariants({
                    size: "sm",
                    variant: "link",
                    className: "!font-bold",
                  })}
                >
                  + Add New
                </Link>
              </div>
              <div className="flex flex-wrap gap-4">
                {patients?.map((patient) => {
                  const isSelected = data?.patient?.id === patient.patientId

                  return (
                    <Button
                      key={patient.patientId}
                      variant="outline"
                      className={cn("rounded-full border-2", {
                        "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground": isSelected,
                      })}
                      onClick={() => handleSelectPatient(patient)}
                    >
                      {patient.name}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          <div>
            <h2 className="mb-4 text-left font-bold">Add Symptoms</h2>
            <Textarea
              placeholder="Type your symptoms here or select from the list below"
              rows={5}
              value={data?.symptoms?.join(", ")}
              onChange={(e) => handleTypeSymptoms(e.target.value)}
            />
            {isSymptomsLoading ? (
              <p>Loading...</p>
            ) : (
              !!symptoms.length && (
                <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-3">
                  {symptoms.map((symptom) => {
                    const isSelected = data?.symptoms?.includes(symptom)
                    return (
                      <li
                        key={symptom}
                        className={cn("cursor-pointer rounded-full border px-4 py-2 text-xs", {
                          "border-primary-2 bg-primary-2 text-primary-foreground": isSelected,
                        })}
                        onClick={() => handleToggleSelectSymptom(symptom)}
                      >
                        {symptom}
                      </li>
                    )
                  })}
                </ul>
              )
            )}
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center bg-white p-4 border-t border-gray-200 shadow-lg">
          <Button className="w-full max-w-sm !font-bold" onClick={handleContinue}>
            Continue
          </Button>
        </div>
      </div>
    </>
  )
}

export default AppointmentPage
